import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Plus, Edit, Trash2, Package, Loader2, Save, Briefcase, CalendarDays, FileSpreadsheet } from 'lucide-react';
import ProductForm from './ProductForm';
import { toast } from 'sonner';
import useShopTools from '../../hooks/useShopTools';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEFAULT_IMAGE = 'https://images.unsplash.com/photo-1595950653106-6c9ebd614d3a?w=200';

const TIPO_META = {
  product: { label: 'Prodotti', single: 'prodotto', color: 'blue', icon: Package },
  service: { label: 'Servizi', single: 'servizio', color: 'emerald', icon: Briefcase },
  event:   { label: 'Eventi', single: 'evento', color: 'orange', icon: CalendarDays },
};

export default function ProductList({ shop }) {
  const navigate = useNavigate();
  const { isEnabled: toolEnabled } = useShopTools();
  const showInventario = toolEnabled('inventario');
  const showCassa = toolEnabled('cassa');
  const showServizi = toolEnabled('servizi');
  const showEventi = toolEnabled('eventi');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formTipo, setFormTipo] = useState('product');
  const [editingProduct, setEditingProduct] = useState(null);
  const [deleteProduct, setDeleteProduct] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all'); // all | product | service | event
  // Inline edits keyed by product id: { id: { prezzo, sconto } }
  const [edits, setEdits] = useState({});
  const [savingId, setSavingId] = useState(null);

  const editValue = (p, field) => {
    const e = edits[p.id];
    if (e && field in e) return e[field];
    return p[field] ?? '';
  };

  const setEdit = (id, field, value) => {
    setEdits((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), [field]: value } }));
  };

  const isDirty = (p) => {
    const e = edits[p.id];
    if (!e) return false;
    const newPrezzo = e.prezzo !== undefined ? (e.prezzo === '' ? null : parseFloat(e.prezzo)) : p.prezzo;
    const newSconto = e.sconto !== undefined ? (e.sconto === '' ? null : parseInt(e.sconto, 10)) : p.sconto;
    return newPrezzo !== p.prezzo || newSconto !== p.sconto;
  };

  const handleSaveInline = async (p) => {
    const e = edits[p.id] || {};
    const payload = {};
    if ('prezzo' in e) payload.prezzo = e.prezzo === '' ? null : parseFloat(e.prezzo);
    if ('sconto' in e) {
      const v = e.sconto === '' ? null : parseInt(e.sconto, 10);
      if (v !== null && (isNaN(v) || v < 0 || v > 100)) {
        toast.error('Sconto tra 0 e 100');
        return;
      }
      payload.sconto = v;
    }
    if (Object.keys(payload).length === 0) return;
    setSavingId(p.id);
    try {
      const res = await axios.patch(`${API}/products/${p.id}`, payload, { withCredentials: true });
      setProducts((prev) => prev.map((x) => (x.id === p.id ? res.data : x)));
      setEdits((prev) => {
        const c = { ...prev };
        delete c[p.id];
        return c;
      });
      toast.success('Salvato');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore salvataggio');
    } finally {
      setSavingId(null);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, [shop]);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products/my`, { withCredentials: true });
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleProductSaved = (product) => {
    if (editingProduct) {
      setProducts(prev => prev.map(p => p.id === product.id ? product : p));
    } else {
      setProducts(prev => [product, ...prev]);
    }
    setShowForm(false);
    setEditingProduct(null);
  };

  const handleDelete = async () => {
    if (!deleteProduct) return;
    
    setDeleteLoading(true);
    try {
      await axios.delete(`${API}/products/${deleteProduct.id}`, { withCredentials: true });
      setProducts(prev => prev.filter(p => p.id !== deleteProduct.id));
    } catch (error) {
      console.error('Error deleting product:', error);
    } finally {
      setDeleteLoading(false);
      setDeleteProduct(null);
    }
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingProduct(null);
  };

  const handleAddNew = (tipo) => {
    setFormTipo(tipo);
    setEditingProduct(null);
    setShowForm(true);
  };

  const handleEdit = (product) => {
    setFormTipo(product.tipo || 'product');
    setEditingProduct(product);
    setShowForm(true);
  };

  if (showForm) {
    return (
      <ProductForm
        product={editingProduct}
        onSave={handleProductSaved}
        onCancel={handleCloseForm}
        tipo={formTipo}
      />
    );
  }

  const filteredProducts = activeFilter === 'all'
    ? products
    : products.filter((p) => (p.tipo || 'product') === activeFilter);

  return (
    <div data-testid="product-list">
      {/* Action tiles — gated by enabled tools (v1.8.0) */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5" data-testid="add-action-tiles">
        {showInventario && (
          <button
            type="button"
            onClick={() => navigate('/inventario')}
            className="aspect-square rounded-2xl bg-emerald-500 hover:bg-emerald-600 text-white flex flex-col items-center justify-center gap-1 shadow-md hover:shadow-lg hover:scale-[1.03] transition-all focus:outline-none focus:ring-4 ring-emerald-200"
            data-testid="add-tile-inventario"
          >
            <FileSpreadsheet className="w-7 h-7" strokeWidth={2.5} />
            <span className="text-xs font-semibold uppercase tracking-wide flex items-center gap-1">
              <Package className="w-3.5 h-3.5" />
              Inventario
            </span>
          </button>
        )}
        {showServizi && <AddTile tipo="service" onClick={() => handleAddNew('service')} />}
        {showEventi && <AddTile tipo="event"   onClick={() => handleAddNew('event')} />}
        {showCassa && (
          <button
            type="button"
            onClick={() => navigate('/cassa')}
            className="aspect-square rounded-2xl bg-slate-900 hover:bg-slate-800 text-white flex flex-col items-center justify-center gap-1 shadow-md hover:shadow-lg hover:scale-[1.03] transition-all focus:outline-none focus:ring-4 ring-slate-300"
            data-testid="tile-cassa"
          >
            <Save className="w-7 h-7" strokeWidth={2.5} />
            <span className="text-xs font-semibold uppercase tracking-wide">Cassa</span>
          </button>
        )}
        {/* Always-visible: add a plain product */}
        <AddTile tipo="product" onClick={() => handleAddNew('product')} />
      </div>

      {/* Filter chips — only show relevant to enabled tools */}
      <div className="flex flex-wrap gap-2 mb-4">
        {['all', 'product', 'service', 'event']
          .filter((f) => f === 'all' || f === 'product' || (f === 'service' && showServizi) || (f === 'event' && showEventi))
          .map((f) => {
          const meta = TIPO_META[f];
          const count = f === 'all' ? products.length : products.filter((p) => (p.tipo || 'product') === f).length;
          const active = activeFilter === f;
          return (
            <button
              key={f}
              type="button"
              onClick={() => setActiveFilter(f)}
              className={`text-xs px-3 py-1.5 rounded-full font-medium transition-all ${
                active ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
              data-testid={`filter-${f}`}
            >
              {f === 'all' ? 'Tutti' : meta?.label} ({count})
            </button>
          );
        })}
      </div>

      {/* Products list */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      ) : filteredProducts.length > 0 ? (
        <div className="space-y-3">
          {filteredProducts.map((product) => (
            <div
              key={product.id}
              className="bg-white rounded-2xl p-4 shadow-sm flex items-center gap-4"
              data-testid={`product-item-${product.id}`}
            >
              {/* Image */}
              <div className="w-20 h-20 rounded-xl overflow-hidden bg-slate-100 flex-shrink-0">
                <img
                  src={product.immagini?.[0] || DEFAULT_IMAGE}
                  alt={product.nome}
                  className="w-full h-full object-cover"
                  onError={(e) => { e.target.src = DEFAULT_IMAGE; }}
                />
              </div>

              {/* Info with inline-editable price/discount */}
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-slate-800 truncate">{product.nome}</h3>
                <p className="text-sm text-slate-500 truncate">{product.descrizione}</p>
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  <div className="flex items-center gap-1">
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={editValue(product, 'prezzo')}
                      onChange={(e) => setEdit(product.id, 'prezzo', e.target.value)}
                      placeholder="€"
                      className="w-20 h-8 text-sm rounded-lg"
                      data-testid={`inline-prezzo-${product.id}`}
                    />
                    <span className="text-xs text-slate-500">€</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={editValue(product, 'sconto')}
                      onChange={(e) => setEdit(product.id, 'sconto', e.target.value)}
                      placeholder="0"
                      className="w-14 h-8 text-sm rounded-lg"
                      data-testid={`inline-sconto-${product.id}`}
                    />
                    <span className="text-xs text-slate-500">% sconto</span>
                  </div>
                  {isDirty(product) && (
                    <Button
                      size="sm"
                      onClick={() => handleSaveInline(product)}
                      disabled={savingId === product.id}
                      className="h-8 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
                      data-testid={`inline-save-${product.id}`}
                    >
                      {savingId === product.id
                        ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        : <><Save className="w-3.5 h-3.5 mr-1" /> Salva</>
                      }
                    </Button>
                  )}
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    product.disponibilita === 'disponibile'
                      ? 'bg-green-100 text-green-700'
                      : 'bg-red-100 text-red-700'
                  }`}>
                    {product.disponibilita === 'disponibile' ? 'Disponibile' : 'Esaurito'}
                  </span>
                  {/* v1.8.7 — publication badge next to availability */}
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      product.pubblicato
                        ? 'bg-emerald-500 text-white'
                        : 'bg-red-500 text-white'
                    }`}
                    data-testid={`publish-badge-${product.id}`}
                  >
                    {product.pubblicato ? 'Pubblicato' : 'Non pubblicato'}
                  </span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2 flex-shrink-0">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleEdit(product)}
                  className="h-9 w-9 text-slate-500 hover:text-blue-600"
                  data-testid={`edit-product-${product.id}`}
                >
                  <Edit className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setDeleteProduct(product)}
                  className="h-9 w-9 text-slate-500 hover:text-red-600"
                  data-testid={`delete-product-${product.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="empty-state bg-white rounded-2xl">
          <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
            <Package className="w-8 h-8 text-slate-400" />
          </div>
          <h3 className="font-outfit font-semibold text-slate-700 mb-2">
            {activeFilter === 'all' ? 'Nessun elemento' : `Nessun ${TIPO_META[activeFilter]?.single || 'elemento'}`}
          </h3>
          <p className="text-slate-500 text-sm">
            Usa le icone in alto per aggiungere prodotti, servizi o eventi
          </p>
        </div>
      )}

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteProduct} onOpenChange={() => setDeleteProduct(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Elimina elemento</AlertDialogTitle>
            <AlertDialogDescription>
              Sei sicuro di voler eliminare "{deleteProduct?.nome}"? Questa azione non può essere annullata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleteLoading}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-button"
            >
              {deleteLoading ? 'Eliminazione...' : 'Elimina'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/** Big colored "+" tile to add a new product/service/event. */
function AddTile({ tipo, onClick }) {
  const meta = TIPO_META[tipo];
  const Icon = meta.icon;
  // Tailwind cannot generate dynamic class names → use a static mapping.
  const styles = {
    blue:     'bg-blue-600 hover:bg-blue-700 ring-blue-200',
    emerald:  'bg-emerald-500 hover:bg-emerald-600 ring-emerald-200',
    orange:   'bg-orange-500 hover:bg-orange-600 ring-orange-200',
  };
  const colorClass = styles[meta.color] || styles.blue;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`aspect-square rounded-2xl ${colorClass} text-white flex flex-col items-center justify-center gap-1 shadow-md hover:shadow-lg hover:scale-[1.03] transition-all focus:outline-none focus:ring-4`}
      data-testid={`add-tile-${tipo}`}
    >
      <Plus className="w-7 h-7" strokeWidth={2.5} />
      <span className="text-xs font-semibold uppercase tracking-wide flex items-center gap-1">
        <Icon className="w-3.5 h-3.5" />
        {meta.single}
      </span>
    </button>
  );
}
